import { useEffect, useRef } from 'react';
import { useStore } from '../../store/store';
import { useTrackingManager } from '../use-tracking-manager';
import {
  onVWBasicInfoMessageLoadEvent,
  onVWBasicFilterSearchResultLoadEvent,
} from '../../tagging/tagging-helpers';

/**
 * Hook to track when search results are loaded.
 * Only tracks when search term changes.
 */
export const useSearchResultsTracking = () => {
  const [state] = useStore();
  const trackingManager = useTrackingManager();
  const previousSearchTermRef = useRef<string>('');

  const {
    qsgNavigation,
    qsgSearchTerm,
    qsgSearchResultsMap,
    qsgSearchLoading,
    qsgSearchErrors,
  } = state.qsg;

  useEffect(() => {
    if (!qsgSearchTerm || previousSearchTermRef.current === qsgSearchTerm)
      return;

    if (!qsgSearchResultsMap || Object.keys(qsgSearchResultsMap).length === 0)
      return;

    previousSearchTermRef.current = qsgSearchTerm;

    const anyResultsKey = Object.keys(qsgSearchResultsMap).find((key) =>
      key.startsWith(`${qsgSearchTerm}:`)
    );
    const anyResults = anyResultsKey
      ? qsgSearchResultsMap[anyResultsKey]
      : null;

    if (anyResults && trackingManager) {
      const isAnyLoading = anyResultsKey
        ? qsgSearchLoading[anyResultsKey] ||
          (!anyResults && !qsgSearchErrors[anyResultsKey])
        : false;

      if (!isAnyLoading) {
        const model = qsgNavigation
          ? {
              modelId: qsgNavigation.modelId,
              modelName: qsgNavigation.modelName,
              modelYear: qsgNavigation.modelYear,
            }
          : undefined;

        if (anyResults.results.length === 0)
          onVWBasicInfoMessageLoadEvent(
            trackingManager,
            `No results returned for search term "${qsgSearchTerm}"`,
            model
          );

        onVWBasicFilterSearchResultLoadEvent(
          trackingManager,
          qsgSearchTerm,
          anyResults.totalCount ?? 0,
          model
        );
      }
    }
  }, [
    qsgSearchTerm,
    qsgSearchResultsMap,
    qsgSearchLoading,
    qsgSearchErrors,
    qsgNavigation,
    trackingManager,
  ]);
};
